import { ipfsToHttp } from "@/lib/image-upload";
import { createFileRoute, ClientOnly, useParams, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient, useMutation, useSuspenseQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAccount, useSendTransaction } from "wagmi";
import { parseEther, formatEther, isAddress } from "viem";
import { executeFeeSplit, buildMintLegs, buildBuyLegs } from "@/lib/fee-split";
import { assertBalance, getExplorerUrl } from "@/lib/chain-utils";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Tag, ShoppingCart, X } from "lucide-react";

export const dropQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["drop", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("nft_drops").select("*").eq("id", id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });

export const listingsQueryOptions = (dropId: string) =>
  queryOptions({
    queryKey: ["listings", dropId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("drop_id", dropId)
        .eq("status", "active")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });


export const dropItemsQueryOptions = (dropId: string) =>
  queryOptions({
    queryKey: ["drop-items", dropId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nft_drop_items")
        .select("id, item_index, name, image_url, attributes, assigned_wallet, assigned_token_id")
        .eq("drop_id", dropId)
        .order("item_index", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

const settingsQueryOptions = () =>
  queryOptions({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data } = await supabase.from("platform_settings").select("*").eq("id", 1).maybeSingle();
      return data;
    },
  });

const networkSettingsQueryOptions = (chainId: number | null | undefined) =>
  queryOptions({
    queryKey: ["network-settings", chainId ?? 0],
    enabled: !!chainId,
    queryFn: async () => {
      if (!chainId) return null;
      const { data } = await supabase.from("platform_network_settings").select("*").eq("chain_id", chainId).maybeSingle();
      return data;
    },
  });


export const Route = createFileRoute("/drop/$id")({
  loader: ({ context, params }) => {
    context.queryClient.ensureQueryData(dropQueryOptions(params.id));
    context.queryClient.prefetchQuery(listingsQueryOptions(params.id));
    context.queryClient.prefetchQuery(settingsQueryOptions());
    context.queryClient.prefetchQuery(dropItemsQueryOptions(params.id));
    
  },
  component: () => (<ClientOnly fallback={null}><DropDetail /></ClientOnly>),
  head: () => ({ meta: [{ title: "Drop - RobotDex" }] }),
  notFoundComponent: () => (
    <div className="p-8">
      Drop not found. <Link to="/market" className="text-primary underline">Back to market</Link>
    </div>
  ),
  errorComponent: ({ error }) => <div className="p-8 text-destructive">{String(error)}</div>,
});

function DropDetail() {
  const { id } = useParams({ from: "/drop/$id" });
  const { data: drop } = useSuspenseQuery(dropQueryOptions(id));
  if (!drop) return <div className="p-8">Drop not found.</div>;
  return <DropView drop={drop} id={id} />;
}

function DropView({ drop, id }: { drop: any; id: string }) {
  const qc = useQueryClient();
  const { address, isConnected } = useAccount();
  const { sendTransactionAsync, isPending } = useSendTransaction();
  const [qty, setQty] = useState(1);

  const { data: settings } = useQuery(settingsQueryOptions());
  const { data: netSettings } = useQuery(networkSettingsQueryOptions(drop.chain_id));
  const { data: listings } = useQuery(listingsQueryOptions(id));
  const { data: items } = useQuery(dropItemsQueryOptions(id));

  const chainFeeRecipient = ((netSettings as any)?.fee_recipient as string | null) ?? (settings?.fee_recipient ?? null);

  

  const priceWei = BigInt(drop.mint_price_wei || "0");
  const totalWei = priceWei * BigInt(qty);
  const soldOut = !!(drop.max_supply && drop.minted_count >= drop.max_supply);
  const now = Date.now();
  const notStarted = !!(drop.start_at && new Date(drop.start_at).getTime() > now);
  const ended = !!(drop.end_at && new Date(drop.end_at).getTime() < now);

  const mintFeePct = ((settings?.mint_fee_bps ?? 500) / 100).toFixed(2);
  const listFeePct = ((settings?.list_fee_bps ?? 500) / 100).toFixed(2);
  const creatorFeePct = ((settings?.creator_fee_bps ?? 250) / 100).toFixed(2);

  async function handleMint() {
    if (!isConnected || !address) return toast.error("Connect your wallet");
    const { signedRecordMint } = await import("@/lib/signed-actions");
    if (!drop.contract_address) {
      try {
        await signedRecordMint(address, {
          drop_id: drop.id,
          qty,
          price_wei: totalWei.toString(),
          kind: "mint_request",
          tx_hash: null,
        });
        toast.success("Mint recorded. Contract deployment pending by creator.");
        qc.invalidateQueries({ queryKey: ["drop", id] }); qc.invalidateQueries({ queryKey: ["drop-items", id] }); qc.invalidateQueries({ queryKey: ["drop-activity", id] });
      } catch (e: any) {
        toast.error(e?.message || "Mint failed");
      }
      return;
    }
    try {
      const payTo = (drop.contract_address && isAddress(drop.contract_address)
        ? drop.contract_address
        : drop.creator_wallet) as `0x${string}`;
      const legs = buildMintLegs({
        totalWei,
        mintFeeBps: settings?.mint_fee_bps ?? 0,
        feeRecipient: chainFeeRecipient,
        payTo,
      });
      // Balance check across all legs before prompting wallet.
      const needed = legs.reduce((a, l) => a + l.value, 0n);
      await assertBalance(address as `0x${string}`, drop.chain_id, needed);
      toast.message(`Confirm ${legs.length} transaction${legs.length > 1 ? "s" : ""} in your wallet…`);
      const { mainHash } = await executeFeeSplit(legs);
      await signedRecordMint(address, {
        drop_id: drop.id,
        qty,
        price_wei: totalWei.toString(),
        tx_hash: mainHash,
        kind: "mint",
      });
      const link = getExplorerUrl(drop.chain_id, mainHash);
      toast.success(legs.length > 1 ? `Mint sent (${legs.length} txs incl. fee split)` : "Mint tx sent!", {
        action: link ? { label: "View on explorer", onClick: () => window.open(link, "_blank") } : undefined,
      });
      qc.invalidateQueries({ queryKey: ["drop", id] }); qc.invalidateQueries({ queryKey: ["drop-items", id] }); qc.invalidateQueries({ queryKey: ["drop-activity", id] });
    } catch (e: any) {
      toast.error(e?.shortMessage || e?.message || "Mint failed");
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <PageHeader title={drop.name} subtitle={`${drop.symbol} · Chain ${drop.chain_id}`} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="overflow-hidden bg-card/60 neon-border">
          <div className="aspect-square bg-gradient-to-br from-primary/20 to-background">
            {drop.logo_url ? (
              <img src={ipfsToHttp(drop.logo_url)} alt={drop.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-6xl font-black neon-text">
                {drop.symbol?.slice(0, 3)}
              </div>
            )}
          </div>
        </Card>
        <Card className="p-6 bg-card/60 space-y-4">
          <div className="flex flex-wrap gap-2">
            {(() => {
              const isLive = !notStarted && !ended && !soldOut;
              return (
                <Badge variant="outline" className={isLive ? "neon-border text-primary" : "neon-border"}>
                  {isLive ? "● LIVE" : soldOut ? "Sold out" : notStarted ? "Upcoming" : ended ? "Ended" : drop.status}
                </Badge>
              );
            })()}
            {drop.max_supply ? (
              <>
                <Badge variant="outline">Minted {drop.minted_count}/{drop.max_supply}</Badge>
                <Badge variant="outline">Remaining {Math.max(0, drop.max_supply - drop.minted_count)}</Badge>
              </>
            ) : (
              <>
                <Badge variant="outline">Unlimited supply</Badge>
                <Badge variant="outline">Minted {drop.minted_count}</Badge>
              </>
            )}
            {items && <Badge variant="outline">{items.filter((i: any) => !i.assigned_wallet).length}/{items.length} items left</Badge>}
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{drop.description || "No description."}</p>
          <div className="text-sm space-y-1">
            <div>
              Price:{" "}
              <span className="text-primary font-semibold">
                {priceWei === 0n ? "Free" : `${formatEther(priceWei)} ETH`}
              </span>
            </div>
            {drop.start_at && <div>Starts: {new Date(drop.start_at).toLocaleString()}</div>}
            {drop.end_at && <div>Ends: {new Date(drop.end_at).toLocaleString()}</div>}
            <div className="text-muted-foreground text-xs">Creator: {drop.creator_wallet}</div>
            {drop.contract_address && (
              <div className="text-muted-foreground text-xs break-all">Contract: {drop.contract_address}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 10, 25, 50].map((n) => (
              <Button
                key={n}
                type="button"
                size="sm"
                variant={qty === n ? "default" : "outline"}
                className={qty === n ? "neon-glow" : "neon-border"}
                onClick={() => setQty(n)}
              >
                {n}
              </Button>
            ))}
            <Input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
              className="w-20"
            />
            <Button onClick={handleMint} disabled={isPending || soldOut || notStarted || ended} className="flex-1 neon-glow">
              {soldOut ? "Sold out" : notStarted ? "Not started" : ended ? "Ended" : isPending ? "Minting…" : `Mint ${qty}`}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Total: {formatEther(totalWei)} ETH · Platform mint fee {mintFeePct}%
          </p>
        </Card>
      </div>

      {items && items.length > 0 && (
        <ItemsGallery items={items} myWallet={address ?? null} />
      )}

      <ListingsSection drop={drop} listings={listings ?? []} listFeePct={listFeePct} creatorFeePct={creatorFeePct} chainFeeRecipient={chainFeeRecipient} />

      <DropActivityFeed dropId={id} items={items ?? []} />
    </div>
  );
}

function ListingsSection({
  drop,
  listings,
  listFeePct,
  creatorFeePct,
  chainFeeRecipient,
}: {
  drop: any;
  listings: any[];
  listFeePct: string;
  creatorFeePct: string;
  chainFeeRecipient: string | null;
}) {
  const qc = useQueryClient();
  const { address, isConnected } = useAccount();
  const { sendTransactionAsync } = useSendTransaction();
  const { data: settings } = useQuery(settingsQueryOptions());
  const [tokenId, setTokenId] = useState("");
  const [priceEth, setPriceEth] = useState("");

  const createListing = useMutation({
    mutationFn: async () => {
      if (!address) throw new Error("Connect wallet");
      const wei = priceEth ? parseEther(priceEth as `${number}`).toString() : "0";
      const { signedCreateListing } = await import("@/lib/signed-actions");
      await signedCreateListing(address, {
        drop_id: drop.id,
        token_id: tokenId || null,
        price_wei: wei,
      });
    },
    onSuccess: () => {
      toast.success("Listing created");
      setTokenId("");
      setPriceEth("");
      qc.invalidateQueries({ queryKey: ["listings", drop.id] });
      qc.invalidateQueries({ queryKey: ["activity"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to list"),
  });

  const cancelListing = useMutation({
    mutationFn: async (listingId: string) => {
      if (!address) throw new Error("Connect wallet");
      const { signedCancelListing } = await import("@/lib/signed-actions");
      await signedCancelListing(address, listingId);
    },
    onSuccess: () => {
      toast.success("Listing cancelled");
      qc.invalidateQueries({ queryKey: ["listings", drop.id] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  async function handleBuy(listing: any) {
    if (!isConnected || !address) return toast.error("Connect your wallet");
    const priceWei = BigInt(listing.price_wei || "0");
    try {
      let mainHash: string | undefined;
      let legCount = 1;
      if (priceWei > 0n) {
        const legs = buildBuyLegs({
          totalWei: priceWei,
          listFeeBps: settings?.list_fee_bps ?? 0,
          creatorFeeBps: settings?.creator_fee_bps ?? 0,
          feeRecipient: chainFeeRecipient,
          creator: drop.creator_wallet,
          seller: listing.seller_wallet as `0x${string}`,
        });
        const needed = legs.reduce((a, l) => a + l.value, 0n);
        await assertBalance(address as `0x${string}`, drop.chain_id, needed);
        toast.message(`Confirm ${legs.length} transaction${legs.length > 1 ? "s" : ""} in your wallet…`);
        const res = await executeFeeSplit(legs);
        mainHash = res.mainHash;
        legCount = legs.length;
      }
      const { signedBuyListing } = await import("@/lib/signed-actions");
      await signedBuyListing(address, { listing_id: listing.id, tx_hash: mainHash ?? null });
      const link = mainHash ? getExplorerUrl(drop.chain_id, mainHash) : null;
      toast.success(mainHash ? (legCount > 1 ? `Purchase sent (${legCount} txs incl. fee split)` : "Purchase tx sent!") : "Marked as sold", {
        action: link ? { label: "View on explorer", onClick: () => window.open(link, "_blank") } : undefined,
      });
      qc.invalidateQueries({ queryKey: ["listings", drop.id] });
    } catch (e: any) {
      toast.error(e?.shortMessage || e?.message || "Buy failed");
    }
  }

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="p-5 bg-card/60 neon-border lg:col-span-1">
        <h3 className="font-semibold flex items-center gap-2"><Tag className="h-4 w-4 text-primary" /> List an NFT</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Platform fee {listFeePct}% + creator royalty {creatorFeePct}% on sale.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Token ID</Label>
            <Input value={tokenId} onChange={(e) => setTokenId(e.target.value)} placeholder="e.g. 42" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Price (ETH)</Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              value={priceEth}
              onChange={(e) => setPriceEth(e.target.value)}
              placeholder="0.05"
            />
          </div>
          <Button
            onClick={() => createListing.mutate()}
            disabled={!isConnected || createListing.isPending || !priceEth}
            className="w-full neon-glow"
          >
            {createListing.isPending ? "Listing…" : "Create listing"}
          </Button>
          {!isConnected && <p className="text-xs text-muted-foreground text-center">Connect wallet to list.</p>}
        </div>
      </Card>

      <Card className="p-5 bg-card/60 neon-border lg:col-span-2">
        <h3 className="font-semibold flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-primary" /> Active listings
        </h3>
        {listings.length === 0 && (
          <p className="text-sm text-muted-foreground mt-4">No listings yet. Be the first to list.</p>
        )}
        <div className="mt-4 divide-y divide-border/60">
          {listings.map((l) => {
            const isSeller = address && l.seller_wallet?.toLowerCase() === address.toLowerCase();
            const priceEth = l.price_wei && l.price_wei !== "0" ? formatEther(BigInt(l.price_wei)) : "0";
            return (
              <div key={l.id} className="py-3 flex items-center gap-3 text-sm flex-wrap">
                <Badge variant="outline" className="neon-border">#{l.token_id ?? "-"}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-primary font-semibold">{priceEth} ETH</div>
                  <div className="text-xs text-muted-foreground truncate">
                    by {l.seller_wallet?.slice(0, 6)}…{l.seller_wallet?.slice(-4)}
                  </div>
                </div>
                {isSeller ? (
                  <Button size="sm" variant="outline" onClick={() => cancelListing.mutate(l.id)} disabled={cancelListing.isPending}>
                    <X className="h-3 w-3 mr-1" /> Cancel
                  </Button>
                ) : (
                  <Button size="sm" className="neon-glow" onClick={() => handleBuy(l)} disabled={!isConnected}>
                    Buy
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function ItemsGallery({ items, myWallet }: { items: any[]; myWallet: string | null }) {
  const mine = myWallet ? items.filter((i) => i.assigned_wallet?.toLowerCase() === myWallet.toLowerCase()) : [];
  const remaining = items.filter((i) => !i.assigned_wallet).length;
  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-semibold text-lg">Collection items <span className="text-muted-foreground text-sm font-normal">({items.length} · {remaining} unassigned)</span></h3>
        {mine.length > 0 && (
          <span className="text-xs text-primary">You own {mine.length} item{mine.length > 1 ? "s" : ""} from this drop</span>
        )}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2">
        {items.map((it) => {
          const isMine = myWallet && it.assigned_wallet?.toLowerCase() === myWallet.toLowerCase();
          return (
            <div key={it.id} className={`relative aspect-square rounded overflow-hidden neon-border ${isMine ? "ring-2 ring-primary" : ""} ${it.assigned_wallet && !isMine ? "opacity-60" : ""}`}>
              <img src={ipfsToHttp(it.image_url)} alt={it.name || `#${it.item_index}`} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute bottom-0 inset-x-0 text-[10px] px-1 py-0.5 bg-background/70 backdrop-blur truncate">
                {it.assigned_wallet ? (isMine ? "Yours" : "Minted") : `#${it.item_index + 1}`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ACTIVITY_PAGE_SIZE = 20;

function DropActivityFeed({ dropId, items }: { dropId: string; items: any[] }) {
  const [kindFilter, setKindFilter] = useState<"all" | "mint" | "buy">("all");
  const [walletFilter, setWalletFilter] = useState("");
  const [walletApplied, setWalletApplied] = useState("");

  const kinds =
    kindFilter === "mint" ? ["mint", "mint_request"]
    : kindFilter === "buy" ? ["buy"]
    : ["mint", "mint_request", "buy"];

  const query = useInfiniteQuery({
    queryKey: ["drop-activity", dropId, kindFilter, walletApplied.toLowerCase()],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      let q = supabase
        .from("activities")
        .select("id, kind, actor_wallet, price_wei, tx_hash, chain_id, metadata, created_at")
        .eq("drop_id", dropId)
        .in("kind", kinds)
        .order("created_at", { ascending: false })
        .range(pageParam, pageParam + ACTIVITY_PAGE_SIZE - 1);
      if (walletApplied) q = q.ilike("actor_wallet", walletApplied.toLowerCase());
      const { data, error } = await q;
      if (error) throw error;
      return { rows: data ?? [], next: (data?.length ?? 0) === ACTIVITY_PAGE_SIZE ? pageParam + ACTIVITY_PAGE_SIZE : null };
    },
    getNextPageParam: (last) => last.next,
  });

  const byId = new Map<string, any>();
  for (const it of items) byId.set(it.id, it);

  const qcLocal = useQueryClient();
  const assigned = items.filter((i) => i.assigned_wallet);
  // Signature that changes only when the set of assigned items or their image URLs change.
  const validationSig = assigned
    .map((i) => `${i.item_index}:${i.assigned_token_id ?? i.item_index}:${i.image_url}`)
    .sort()
    .join("|");
  const validationKey = ["metadata-validation", dropId, validationSig] as const;

  const cachedValidation = qcLocal.getQueryData<{ pass: number; fail: number; issues: string[] }>(validationKey);
  const [validation, setValidation] = useState<{ pass: number; fail: number; issues: string[] } | null>(cachedValidation ?? null);
  const [validating, setValidating] = useState(false);

  async function runMetadataValidation(force = false) {
    setValidating(true);
    try {
      if (force) qcLocal.removeQueries({ queryKey: validationKey });
      const result = await qcLocal.fetchQuery({
        queryKey: validationKey,
        staleTime: Infinity,
        gcTime: 30 * 60_000,
        queryFn: async () => {
          const issues: string[] = [];
          let pass = 0;
          for (const it of assigned) {
            const tokenId = it.assigned_token_id ?? String(it.item_index);
            const url = `/api/public/metadata/${dropId}/${tokenId}`;
            try {
              const res = await fetch(url);
              if (!res.ok) { issues.push(`#${it.item_index + 1}: HTTP ${res.status}`); continue; }
              const text = await res.text();
              const m = JSON.parse(text);
              const problems: string[] = [];
              if (!m.name || !m.name.includes(`#${it.item_index + 1}`)) problems.push("name mismatch");
              if (!m.symbol) problems.push("missing symbol");
              if (!m.description) problems.push("missing description");
              if (!m.image || m.image !== ipfsToHttp(it.image_url)) problems.push("image mismatch");
              if (!Array.isArray(m.attributes)) problems.push("attributes missing");
              const res2 = await fetch(url);
              const text2 = await res2.text();
              if (text !== text2) problems.push("unstable tokenURI");
              if (problems.length) issues.push(`#${it.item_index + 1}: ${problems.join(", ")}`);
              else pass++;
            } catch (e: any) {
              issues.push(`#${it.item_index + 1}: ${e.message ?? "fetch failed"}`);
            }
          }
          return { pass, fail: assigned.length - pass, issues: issues.slice(0, 10) };
        },
      });
      setValidation(result);
    } finally {
      setValidating(false);
    }
  }

  const allRows = (query.data?.pages ?? []).flatMap((p) => p.rows);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-semibold text-lg">Recent activity</h3>
        <div className="flex items-center gap-2">
          {validation && !validating ? (
            <span className="text-xs text-muted-foreground">
              cached: {validation.pass} ok / {validation.fail} fail
            </span>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => runMetadataValidation(false)}
            disabled={validating || assigned.length === 0}
          >
            {validating ? "Validating…" : validation ? "Show cached" : "Validate metadata"}
          </Button>
          {validation && !validating ? (
            <Button size="sm" variant="ghost" onClick={() => runMetadataValidation(true)} disabled={validating}>
              Revalidate
            </Button>
          ) : null}
        </div>
      </div>



      <Card className="p-4 bg-card/60 neon-border">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="inline-flex rounded-md border border-border/60 p-0.5 text-xs">
            {(["all", "mint", "buy"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKindFilter(k)}
                className={`px-3 py-1 rounded ${kindFilter === k ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                {k === "all" ? "All" : k === "mint" ? "Mints" : "Purchases"}
              </button>
            ))}
          </div>
          <Input
            value={walletFilter}
            onChange={(e) => setWalletFilter(e.target.value)}
            placeholder="Filter by wallet address (0x…)"
            className="h-8 max-w-xs text-xs"
            onKeyDown={(e) => { if (e.key === "Enter") setWalletApplied(walletFilter.trim()); }}
          />
          <Button size="sm" variant="outline" onClick={() => setWalletApplied(walletFilter.trim())}>Apply</Button>
          {walletApplied && (
            <Button size="sm" variant="ghost" onClick={() => { setWalletFilter(""); setWalletApplied(""); }}>Clear</Button>
          )}
        </div>

        {validation && (
          <div className={`mb-3 rounded border p-3 text-xs ${validation.fail === 0 ? "border-primary/40 text-primary" : "border-destructive/40 text-destructive"}`}>
            Metadata check: {validation.pass} passed, {validation.fail} failed.
            {validation.issues.length > 0 && (
              <ul className="mt-1 list-disc list-inside">
                {validation.issues.map((i, idx) => <li key={idx}>{i}</li>)}
              </ul>
            )}
          </div>
        )}

        {query.isLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : allRows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No matching events.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {allRows.map((a: any) => {
              const kindLabel = a.kind === "buy" ? "Purchase" : a.kind === "mint_request" ? "Mint (pending)" : "Mint";
              const ids: string[] = Array.isArray(a.metadata?.assigned_item_ids) ? a.metadata.assigned_item_ids : [];
              const assigned = ids.map((id) => byId.get(id)).filter(Boolean);
              const priceEth = a.price_wei && a.price_wei !== "0" ? formatEther(BigInt(a.price_wei)) : "0";
              const qty = a.metadata?.qty ?? 1;
              const explorer = a.tx_hash ? getExplorerUrl(a.chain_id, a.tx_hash) : null;
              return (
                <div key={a.id} className="py-3 flex items-center gap-3 text-sm flex-wrap">
                  <Badge variant="outline" className="neon-border">{kindLabel}</Badge>
                  <div className="flex -space-x-2">
                    {assigned.slice(0, 4).map((it: any) => (
                      <img key={it.id} src={ipfsToHttp(it.image_url)} alt={it.name} className="w-9 h-9 rounded object-cover border border-border/60" />
                    ))}
                    {assigned.length > 4 && (
                      <div className="w-9 h-9 rounded bg-muted flex items-center justify-center text-xs">+{assigned.length - 4}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      {a.actor_wallet ? `${a.actor_wallet.slice(0, 6)}…${a.actor_wallet.slice(-4)}` : "-"}
                      {qty > 1 && <span className="text-muted-foreground"> · x{qty}</span>}
                      {assigned.length > 0 && (
                        <span className="text-muted-foreground"> · got {assigned.map((it: any) => `#${(it.item_index ?? 0) + 1}`).join(", ")}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      {a.price_wei && a.price_wei !== "0" ? <span>{priceEth} ETH</span> : null}
                      <span>{new Date(a.created_at).toLocaleString()}</span>
                      {explorer && (
                        <a href={explorer} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          View tx ↗
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {query.hasNextPage && (
          <div className="pt-3 flex justify-center">
            <Button size="sm" variant="outline" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
